import axios from 'axios';
import { MyPurchaseQueryParams } from 'config/types/catalog.type';
import {
  CartRequestMeta,
  CartResponse,
  GetCheckoutInput,
  getCountryLocationsForSettingsResponse,
  GetUserProfileResponse,
  getUserPurchasesResponse,
} from 'config/types/user.type';
import NavigationUtils from 'config/utils/navigation.util';
import queryString from 'query-string';
import { Item } from 'store/models/cart.model';
import { StoreOrder } from 'store/models/order.model';
import { Product } from '../../../store/models/catalog.model';
import { Address, User } from '../../../store/models/user.model';
import { URL_PATHS } from '../constants/api.constants';
import { GetOrderDetailsResponse } from 'config/types/order.type';

const getUserProfile = (): Promise<GetUserProfileResponse> => {
  return axios.get(`${URL_PATHS.API_USER_V1}/profile`);
};

const updateUserProfile = (profileData: User): Promise<Record<any, any>> => {
  return axios.put(`${URL_PATHS.API_USER_V1}/profile`, profileData);
};

const selectUserAddress = (addressId: string): Promise<Record<any, any>> => {
  return axios.get(`${URL_PATHS.API_USER_V1}/address/${addressId}/select`);
};

const deleteUserAddress = (addressId: string): Promise<Record<any, any>> => {
  return axios.delete(`${URL_PATHS.API_USER_V1}/address/${addressId}`);
};

const createUserAddress = (addressData: Address): Promise<Record<any, any>> => {
  return axios.post(`${URL_PATHS.API_USER_V1}/address`, addressData);
};

const getFavorites = (favoritesIds: string[]): Promise<Product[]> => {
  const joinFavoriteIds: string = favoritesIds.join('-');
  return axios.get(`${URL_PATHS.API_USER_V1}/favorites?products=${joinFavoriteIds}`);
};

const getUserPurchases = (myPurchaseQueryParams: MyPurchaseQueryParams): Promise<getUserPurchasesResponse> => {
  return axios.get(
    `${URL_PATHS.API_USER_V1}/purchases?${queryString.stringify(myPurchaseQueryParams, NavigationUtils.queryStringOptions)}`,
  );
};

const getPurchaseByPurchaseCode = (purchaseCode: string): Promise<GetOrderDetailsResponse> => {
  return axios.get(`${URL_PATHS.API_USER_V1}/purchase/${purchaseCode}`);
};

const getCheckout = (getCheckoutInput: GetCheckoutInput): Promise<StoreOrder & CartResponse> => {
  const { items, selectedAddressForCheckout, isDelivery, isPickup } = getCheckoutInput;
  const getcheckoutPostData = { items, selectedAddressForCheckout, isDelivery, isPickup };
  return axios.post(`${URL_PATHS.API_USER_V1}/checkout`, getcheckoutPostData);
};

const validateDni = (dni: string): Promise<{ valid: true }> => {
  return axios.post(`${URL_PATHS.API_USER_V1}/validate-dni`, { dni });
};

const getCountryLocationsFromSettings = (): Promise<getCountryLocationsForSettingsResponse> => {
  return axios.get(`${URL_PATHS.API_SETTING_V1}/country-locations`);
};

const mergeCart = async (items: Item[], meta?: CartRequestMeta): Promise<CartResponse> => {
  return axios.post(`${URL_PATHS.API_USER_V1}/cart-merge`, { items, ...meta });
};

const syncCart = async (items: Item[], meta?: CartRequestMeta): Promise<CartResponse> => {
  return axios.post(`${URL_PATHS.API_USER_V1}/cart-sync`, { items, ...meta });
};

const checkUserCart = (meta?: CartRequestMeta): Promise<CartResponse> => {
  const query = meta?.cartOpId ? `?cartOpId=${encodeURIComponent(meta.cartOpId)}` : '';
  return axios.get(`${URL_PATHS.API_USER_V1}/cart-check${query}`);
};

const UserService = {
  getUserProfile,
  selectUserAddress,
  deleteUserAddress,
  createUserAddress,
  updateUserProfile,
  getFavorites,
  getUserPurchases,
  getPurchaseByPurchaseCode,
  getCheckout,
  validateDni,
  getCountryLocationsFromSettings,
  mergeCart,
  syncCart,
  checkUserCart,
};

export default UserService;
